import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import usePlanAccess from '../../hooks/usePlanAccess';
import SubscriptionLock from './SubscriptionLock';
import { CONST_ROUTE_PATH } from '../../route/RouteConstants';

const UtilBar = ({ avgValue, peakValue }) => {
  if (avgValue === null && peakValue === null) {
    return <span className="util-text">—</span>;
  }

  const avg = avgValue ?? 0;
  const peak = peakValue ?? avg;
  const cls = avg < 30 ? 'low' : avg < 70 ? 'med' : 'high';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
      {/* Three-Part Bar */}
      <div style={{
        width: '100%',
        height: '6px',
        background: 'var(--line-soft)',
        borderRadius: '4px',
        overflow: 'hidden',
        display: 'flex',
      }}>
        {/* Orange: Average usage */}
        <div
          style={{
            width: `${avg}%`,
            background: 'var(--brand)',
            height: '100%',
          }}
        />
        
        {/* Red: Peak variance (from avg to peak) */}
        <div
          style={{
            width: `${peak - avg}%`,
            background: 'var(--red)',
            height: '100%',
          }}
        />
        
        {/* Green: Unused capacity (from peak to 100) */}
        <div
          style={{
            flex: 1,
            background: 'var(--green)',
            height: '100%',
          }}
        />
      </div>

      {/* Labels - Clear and Explicit */}
      <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <div style={{ color: 'var(--text)' }}>
          Avg: <span style={{ color: 'var(--brand)', fontWeight: 600 }}>{avg.toFixed(2)}%</span>
        </div>
        <div style={{ color: 'var(--text)' }}>
          Peak: <span style={{ color: 'var(--red)', fontWeight: 600 }}>{peak.toFixed(2)}%</span>
        </div>
      </div>
    </div>
  );
};

// ── Skeleton Row ───────────────────────────────────────────
const SkeletonRow = ({ colCount }) => (
  <tr>
    {Array.from({ length: colCount }).map((_, i) => (
      <td key={i} style={{ padding: '10px 12px' }}>
        <div style={{
          height: '12px',
          borderRadius: '6px',
          background: 'linear-gradient(90deg, #f0f2f7 25%, #e2e6ef 50%, #f0f2f7 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.4s infinite',
          width: i === 0 ? '20px' : i === 1 ? '120px' : i === 3 ? '70px' : i === 6 || i === 7 ? '80px' : '90px',
        }} />
      </td>
    ))}
  </tr>
);

// ── Sort Icon ──────────────────────────────────────────────
const SortIcon = ({ direction }) => {
  if (!direction) return (
    <span style={{ marginLeft: '4px', opacity: 0.3, fontSize: '10px', userSelect: 'none' }}>⇅</span>
  );
  return (
    <span style={{ marginLeft: '4px', fontSize: '10px', userSelect: 'none' }}>
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  );
};

// ── Sort Value Extractor ───────────────────────────────────
const getSortValue = (row, key) => {
  const val = row[key];
  if (val === null || val === undefined || val === '—') return -Infinity;
  if (typeof val === 'number') return val;
  const num = parseFloat(String(val).replace(/[$,%]/g, ''));
  if (!isNaN(num)) return num;
  return String(val).toLowerCase();
};

// ── Dropdown Wrapper ───────────────────────────────────────
const Dropdown = ({ anchorRef, open, onClose, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div ref={ref} style={{
      position: 'absolute',
      top: '100%',
      left: 0,
      zIndex: 999,
      background: 'var(--surface)',
      border: '1px solid var(--line)',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
      minWidth: '180px',
      padding: '8px 0',
      marginTop: '4px',
    }}>
      {children}
    </div>
  );
};

// ── Table ──────────────────────────────────────────────────
const Table = ({
  rows = [],
  columns = [],
  loading = false,
  skeletonRows = 8,
  pageSizes = [10, 25, 50, 100],
  defaultPageSize = 25,
  defaultSortKey = null,
  defaultSortDir = 'desc',
  emptyText = 'No data found.',
  headerExtra = null,
  footerExtra = null,
  showToolbar = true,
  onExport = null,
  thClassName = '',
  cardClassName = 'card middle',
  exportFilename = null,
  subscriptionGated = false,
  // Set when the current plan excludes this table's content - gets the same
  // teaser-then-blur treatment as a missing subscription.
  planLocked = false,
}) => {
  const { isLocked, maxRecords, isTrial } = usePlanAccess();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortKey ? defaultSortDir : 'asc');

  // ── Columns visibility ───────────────────────────────────
  const [hiddenCols, setHiddenCols] = useState({});
  const [colsOpen, setColsOpen] = useState(false);
  const colsBtnRef = useRef(null);

  // ── Filters ──────────────────────────────────────────────
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const filtersBtnRef = useRef(null);

  // ── Density ──────────────────────────────────────────────
  const [densityOpen, setDensityOpen] = useState(false);
  const [density, setDensity] = useState('normal');
  const densityBtnRef = useRef(null);

  const densityPadding = {
    compact: '4px 12px',
    normal: '10px 12px',
    comfortable: '16px 12px',
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rows, pageSize, sortKey, sortDir, filterValues]);

  // ── Visible columns ──────────────────────────────────────
  const visibleColumns = columns.filter((col) => !hiddenCols[col.key]);

  const toggleCol = (key) => {
    setHiddenCols((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Either reason locks the table: no live subscription at all, or a plan
  // that doesn't include this content.
  const showLock = (subscriptionGated && isLocked) || planLocked;

  // ── Plan record cap ──────────────────────────────────────
  // A trial only ever sees the first N rows. Capped before filter/sort/export
  // so the limit holds everywhere, not just on the rendered page.
  const isCapped = maxRecords != null && rows.length > maxRecords;
  const cappedRows = isCapped ? rows.slice(0, maxRecords) : rows;

  // ── Filter logic ─────────────────────────────────────────
  const filteredRows = cappedRows.filter((row) => {
    return Object.entries(filterValues).every(([key, val]) => {
      if (!val || val.trim() === '') return true;
      const cellVal = String(row[key] ?? '').toLowerCase();
      return cellVal.includes(val.toLowerCase().trim());
    });
  });

  // ── Sort logic ───────────────────────────────────────────
  const sortedRows = sortKey
    ? [...filteredRows].sort((a, b) => {
        const av = getSortValue(a, sortKey);
        const bv = getSortValue(b, sortKey);
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      })
    : filteredRows;

  const totalRows = sortedRows.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);
  const visibleRows = sortedRows.slice(startIndex, endIndex);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const handleExport = () => {
    if (onExport) {
      onExport(sortedRows);
      return;
    }
    // built-in CSV export
    const exportCols = visibleColumns.filter((c) => c.key !== 'checkbox' && c.key !== 'action');
    const header = exportCols.map((c) => (typeof c.label === 'string' ? c.label : c.key)).join(',');
    const rowsCSV = sortedRows.map((row) =>
      exportCols.map((c) => {
        const val = row[c.key] ?? '';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      }).join(',')
    );
    const csv = [header, ...rowsCSV].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    if (exportFilename) {
      a.download = exportFilename;
    } else {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      a.download = `export_${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.csv`;
    }
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = Object.values(filterValues).filter((v) => v && v.trim() !== '').length;
  const colCount = visibleColumns.length;

  const toolbarBtnStyle = (active) => ({
    background: active ? 'var(--primary-light, #e8f0fe)' : 'transparent',
    border: active ? '1px solid var(--primary, #2196f3)' : '1px solid var(--border, #e2e6ef)',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    color: active ? 'var(--primary, #2196f3)' : 'var(--text, #333)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
  });

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        th.sortable {
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .toolbar-btn-group {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .dropdown-item {
          padding: 6px 14px;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text, #333);
          white-space: nowrap;
        }
        .dropdown-item:hover {
          background: var(--hover, rgba(0,0,0,0.04));
        }
        .dropdown-divider {
          height: 1px;
          background: var(--border, #e2e6ef);
          margin: 4px 0;
        }
        .filter-row td {
          padding: 4px 6px !important;
          background: var(--filter-row-bg, #f8f9fc);
        }
        .filter-input {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid var(--border, #e2e6ef);
          borderRadius: 4px;
          padding: 3px 6px;
          font-size: 11px;
          background: var(--card-bg, #fff);
          color: var(--text, #333);
          outline: none;
        }
        .filter-input:focus {
          border-color: var(--primary, #2196f3);
        }
        .density-active {
          font-weight: 600;
          color: var(--primary, #2196f3) !important;
        }
        table th, table td {
          text-align: left !important;
        }
        table th.num, table td.num {
          text-align: right !important;
        }
        table td.table-empty-cell {
          text-align: center !important;
        }
      `}</style>

      <div className={cardClassName}>

        {/* Header extra */}
        {!loading && headerExtra && (
          <div>{headerExtra}</div>
        )}

        {/* Toolbar */}
        {showToolbar && (
          <div
            className="toolbar"
            style={{
              position: 'relative',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {/* Columns toggle */}
            <div style={{ position: 'relative' }}>
              <button
                ref={colsBtnRef}
                style={toolbarBtnStyle(colsOpen || Object.values(hiddenCols).some(Boolean))}
                onClick={() => { setColsOpen((o) => !o); setFiltersOpen(false); setDensityOpen(false); }}
              >
                ▦ Columns
                {Object.values(hiddenCols).some(Boolean) && (
                  <span style={{
                    background: 'var(--primary, #2196f3)',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '0 5px',
                    fontSize: '10px',
                    marginLeft: '2px',
                  }}>
                    {Object.values(hiddenCols).filter(Boolean).length}
                  </span>
                )}
              </button>
              <Dropdown anchorRef={colsBtnRef} open={colsOpen} onClose={() => setColsOpen(false)}>
                <div className="dropdown-item" style={{ fontWeight: 600, color: 'var(--muted)', fontSize: '11px', cursor: 'default' }}>
                  Toggle columns
                </div>
                <div className="dropdown-divider" />
                {columns.map((col) => (
                  <div
                    key={col.key}
                    className="dropdown-item"
                    onClick={() => toggleCol(col.key)}
                  >
                    <span style={{
                      width: '14px',
                      height: '14px',
                      border: '1px solid var(--border, #e2e6ef)',
                      borderRadius: '3px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      background: !hiddenCols[col.key] ? 'var(--primary, #2196f3)' : 'transparent',
                      color: '#fff',
                      flexShrink: 0,
                    }}>
                      {!hiddenCols[col.key] ? '✓' : ''}
                    </span>
                    {typeof col.label === 'string' ? col.label : col.key}
                  </div>
                ))}
                <div className="dropdown-divider" />
                <div
                  className="dropdown-item"
                  onClick={() => setHiddenCols({})}
                  style={{ color: 'var(--primary, #2196f3)' }}
                >
                  Show all
                </div>
              </Dropdown>
            </div>

            {/* Filters toggle */}
            <div style={{ position: 'relative' }}>
              <button
                ref={filtersBtnRef}
                style={toolbarBtnStyle(filtersOpen || activeFilterCount > 0)}
                onClick={() => { setFiltersOpen((o) => !o); setColsOpen(false); setDensityOpen(false); }}
              >
                ⏷ Filters
                {activeFilterCount > 0 && (
                  <span style={{
                    background: 'var(--primary, #2196f3)',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '0 5px',
                    fontSize: '10px',
                    marginLeft: '2px',
                  }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <Dropdown anchorRef={filtersBtnRef} open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                <div className="dropdown-item" style={{ fontWeight: 600, color: 'var(--muted)', fontSize: '11px', cursor: 'default' }}>
                  Filter by column
                </div>
                <div className="dropdown-divider" />
                {columns
                  .filter((col) => !['checkbox', 'action', 'cpu', 'mem'].includes(col.key))
                  .map((col) => (
                    <div key={col.key} style={{ padding: '4px 14px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <label style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {typeof col.label === 'string' ? col.label : col.key}
                      </label>
                      <input
                        className="filter-input"
                        value={filterValues[col.key] ?? ''}
                        onChange={(e) => setFilterValues((prev) => ({ ...prev, [col.key]: e.target.value }))}
                        placeholder={`Search...`}
                      />
                    </div>
                  ))}
                <div className="dropdown-divider" />
                <div
                  className="dropdown-item"
                  onClick={() => setFilterValues({})}
                  style={{ color: 'var(--red, #e53935)' }}
                >
                  ✕ Clear all filters
                </div>
              </Dropdown>
            </div>

            {/* Density toggle */}
            <div style={{ position: 'relative' }}>
              <button
                ref={densityBtnRef}
                style={toolbarBtnStyle(densityOpen)}
                onClick={() => { setDensityOpen((o) => !o); setColsOpen(false); setFiltersOpen(false); }}
              >
                ≡ Density
              </button>
              <Dropdown anchorRef={densityBtnRef} open={densityOpen} onClose={() => setDensityOpen(false)}>
                <div className="dropdown-item" style={{ fontWeight: 600, color: 'var(--muted)', fontSize: '11px', cursor: 'default' }}>
                  Row density
                </div>
                <div className="dropdown-divider" />
                {['compact', 'normal', 'comfortable'].map((d) => (
                  <div
                    key={d}
                    className={`dropdown-item ${density === d ? 'density-active' : ''}`}
                    onClick={() => { setDensity(d); setDensityOpen(false); }}
                  >
                    {density === d ? '● ' : '○ '}
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </div>
                ))}
              </Dropdown>
            </div>

            {/* Export */}
            <button
              style={{ ...toolbarBtnStyle(false), ...(totalRows === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
              onClick={handleExport}
              disabled={totalRows === 0}
            >
              ⬇ Export
            </button>

            {/* Active filter summary */}
            {activeFilterCount > 0 && (
              <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: '4px' }}>
                Showing {totalRows} of {rows.length} rows
              </span>
            )}
          </div>
        )}

        {/* Table */}
        <div style={{ position: 'relative' }}>
        <div className="table-wrap">
          <table>
            <colgroup>
              {visibleColumns.map((col) => (
                <col key={col.key} style={col.minWidth ? { minWidth: `${col.minWidth}px` } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`${thClassName} ${col.className ?? ''} sortable`.trim()}
                    onClick={() => handleSort(col.key)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {col.label}
                    <SortIcon direction={sortKey === col.key ? sortDir : null} />
                  </th>
                ))}
              </tr>
              {/* Inline filter row */}
              {filtersOpen && (
                <tr className="filter-row">
                  {visibleColumns.map((col) => (
                    <td key={col.key}>
                      {!['checkbox', 'action', 'cpu', 'mem'].includes(col.key) ? (
                        <input
                          className="filter-input"
                          value={filterValues[col.key] ?? ''}
                          onChange={(e) => setFilterValues((prev) => ({ ...prev, [col.key]: e.target.value }))}
                          placeholder="Filter..."
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : null}
                    </td>
                  ))}
                </tr>
              )}
            </thead>
            {showLock
              ? (
                <>
                  {/* First 5 real rows — fully visible as teaser */}
                  <tbody>
                    {sortedRows.slice(0, 5).map((row, rowIndex) => (
                      <tr key={row.id ?? rowIndex}>
                        {visibleColumns.map((col) => (
                          <td
                            key={col.key}
                            className={col.className ?? ''}
                            style={{ padding: densityPadding[density] }}
                          >
                            {col.render ? col.render(row) : row[col.key] ?? '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {/* Dummy rows — fake █ chars only, no real data in DOM (safe from DevTools inspect) */}
                  <tbody style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.75 }}>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <tr key={`lock-dummy-${i}`}>
                        {visibleColumns.map((col, j) => (
                          <td key={col.key} style={{ padding: densityPadding[density] }}>
                            {'█'.repeat(5 + ((i * 7 + j * 3) % 10))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </>
              )
              : (
                <tbody>
                  {loading
                    ? Array.from({ length: skeletonRows }).map((_, i) => (
                        <SkeletonRow key={i} colCount={colCount} />
                      ))
                    : visibleRows.length > 0
                      ? visibleRows.map((row, rowIndex) => (
                          <tr key={row.id ?? rowIndex}>
                            {visibleColumns.map((col) => (
                              <td
                                key={col.key}
                                className={col.className ?? ''}
                                style={{ padding: densityPadding[density] }}
                              >
                                {col.render
                                  ? col.render(row)
                                  : row[col.key] ?? '—'}
                              </td>
                            ))}
                          </tr>
                        ))
                      : (
                        <tr>
                          <td
                            colSpan={colCount}
                            className="table-empty-cell"
                            style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}
                          >
                            {emptyText}
                          </td>
                        </tr>
                      )
                  }
                </tbody>
              )
            }
          </table>
        </div>{/* end table-wrap */}

          {/* Lock overlay — outside scroll container so it spans full card width */}
          {showLock && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '68%',
              minHeight: '260px',
              background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,249,252,0.96) 18%, rgba(248,249,252,1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
              pointerEvents: 'none',
            }}>
              <div style={{ pointerEvents: 'all' }}>
                <SubscriptionLock />
              </div>
            </div>
          )}
        </div>{/* end position:relative wrapper */}

        {/* Trial record cap notice */}
        {!loading && isCapped && !showLock && (
          <div className="plan-cap-note">
            Showing the first {maxRecords.toLocaleString()} of {rows.length.toLocaleString()} records
            {isTrial ? ' on your free trial.' : ' on your current plan.'}
            {' '}
            <button
              type="button"
              className="plan-cap-note-link"
              onClick={() => navigate(CONST_ROUTE_PATH.PRICING)}
            >
              Upgrade for full data →
            </button>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination">
          {!loading && !showLock && (
            <>
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {pageSizes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <span>
                {totalRows === 0
                  ? '0 results'
                  : `${startIndex + 1}–${endIndex} of ${totalRows}`
                }
              </span>

              <span
                onClick={handlePrev}
                style={{
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.4 : 1,
                  userSelect: 'none',
                }}
              >
                ‹
              </span>
              <span
                onClick={handleNext}
                style={{
                  cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages || totalPages === 0 ? 0.4 : 1,
                  userSelect: 'none',
                }}
              >
                ›
              </span>
            </>
          )}
        </div>

        {/* Footer extra */}
        {footerExtra && <div>{footerExtra}</div>}

      </div>
    </>
  );
};

export { UtilBar };
export default Table;